import React, { useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import notification from 'epn-ui/umd/dist/components/Notification';
import Button from 'epn-ui/umd/dist/components/Button';

import { faSlidersV } from '@fortawesome/pro-regular-svg-icons/faSlidersV';
import { GetStaticPaths, GetStaticProps } from 'next';
import Loader from 'epn-ui/umd/dist/components/Loader';
import Grid from 'epn-ui/umd/dist/components/Grid';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import BaseEmpty from '@components/base/BaseEmpty';
import useI18n, { getLocalePaths } from '@i18n';
import copyTextToClipboard from '@utils/clipboard';

import FilterButton from '@pages/coupons/FilterButton';
import Filters from '@pages/coupons/Filters';
import Coupon from '@pages/coupons/Coupon';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';

import api from '@api';
import { CREATE_CREATIVE_ERRORS } from '@api/errors';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import { logAmplitudeEvent, AMPLITUDE_EVENTS_KEYS } from 'src/utils/metrics/amplitude';
import { ALI_ID } from '@components/pages/creatives/config';
import { IS_ALI_LINK } from '@utils/regex';

import {
    LIMIT,
    couponLinksCache,
    LOADING_STUBS,
    defaultFiltersValue,
    OFFERS_FIELDS,
    filtersConversionFuncs,
} from '@pages/coupons/config';
import {
    getKey,
    prepareSelectOptions,
    collectObjectWithIds,
    formatCoupons,
    addParamsToDeeplink,
    reduceLink,
} from '@pages/coupons/utils';
import {
    IGetCouponsData,
    IGetCouponsDataFormatted,
    IGetCouponsTypesDataFormatted,
    IGetCreativesData,
    IGetOffersDataFormatted,
} from '@api/types';
import ModalFullDescription from '@components/pages/coupons/ModalFullDescription';
import ModalSelectPlacement from '@components/pages/coupons/ModalSelectPlacement';
import {
    ICouponFiltersValues,
    ICouponLoadingFlags,
    ICouponProps,
    ICouponSelectsOptions,
} from '@pages/coupons/interfaces';
import useFilters from '@hooks/useFilters';
import { CustomGetStaticPathsResult, CustomGetStaticPropsResult } from '../../../../types';

const { useCouponsOffers, useCouponsTypes, useOffers } = apiHooks;

const CouponsPage: React.FC = () => {
    const { t, locale } = useI18n('Coupons');
    const { t: tCommon } = useI18n('Common');
    const { t: tErrors } = useI18n('Errors');

    const [filtersIsVisible, toggleFilters] = useState<boolean>(false);
    const [filtersDirty, setFiltersDirty] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [couponForModal, setCouponForModal] = useState<IGetCouponsDataFormatted | null>(null);
    const [placementModal, setPlacementModal] = useState(null);
    const [hasNext, setHasNext] = useState<boolean>(false);
    const [isCouponsLoading, setIsCouponsLoading] = useState<boolean>(true);

    const filterOptions = {
        conversionFuncs: filtersConversionFuncs,
    };
    const [filters, setFilters] = useFilters(defaultFiltersValue, filterOptions);

    const toggleFullDescriptionModal = (couponData: IGetCouponsDataFormatted): void => {
        setCouponForModal(couponData);
        setIsModalOpen(!isModalOpen);
    };

    const { data: couponOfferIds, isLoading: isLoadingCouponOfferIds } = useCouponsOffers({
        dependencies: [locale],
    });
    const { data: offers = [], isLoading: isLoadingOffers, error: offersError } = useOffers({
        condition: !!couponOfferIds,
        params: {
            ids: couponOfferIds,
            limit: (couponOfferIds as string)?.split(',').length,
            fields: OFFERS_FIELDS,
            lang: locale,
            viewRules: 'role_user',
        },
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;
    const { data: types, isLoading: isLoadingTypes, error: typesErrors } = useCouponsTypes({
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetCouponsTypesDataFormatted[]>;

    const offersObject = collectObjectWithIds(offers || [], 'logo');
    const typesObject = collectObjectWithIds(types || [], 'description');

    const isDataForCouponsReceived = useMemo(
        () => !!offers?.length && !isLoadingOffers && !!types?.length && !isLoadingTypes,
        [offers, types, isLoadingOffers, isLoadingTypes],
    );

    const { data: couponsData = [], error: couponsError, size, setSize } = useSWRInfinite(
        isDataForCouponsReceived
            ? (index: number, previousPageData: IGetCouponsDataFormatted[]) =>
                  getKey(index, previousPageData, filters, locale, ...Object.values(offersObject))
            : null,
        async (
            _,
            offset,
            filtersValue: ICouponFiltersValues,
        ): Promise<IGetCouponsDataFormatted[]> => {
            setIsCouponsLoading(true);
            const onlyValidFilters = Object.keys(filtersValue).reduce(
                (acc, current: string): Partial<ICouponFiltersValues> => ({
                    ...acc,
                    ...(filters[current] && { [current]: filters[current] }),
                }),
                {},
            );

            const params = {
                limit: LIMIT,
                offset,
                ...onlyValidFilters,
                withPromocode: filtersValue.withPromocode,
            };

            const { data, meta } = (await api.coupon.getCoupons({
                params,
            })) as ICreateUseRequestInfiniteResponse<IGetCouponsData[]>;

            if (!data) {
                setIsCouponsLoading(false);
                return [];
            }

            setHasNext(meta?.hasNext as boolean);

            const formattedCoupons: IGetCouponsDataFormatted[] = data
                .map((coupon: IGetCouponsData): IGetCouponsDataFormatted => formatCoupons(coupon))
                .reduce((acc: ICouponProps[], coupon: IGetCouponsDataFormatted) => {
                    const { offerId: localOfferId, dateFrom, dateTo, typeId: localTypeId } = coupon;

                    let findedCoupon;

                    // Отфильтровываем купоны, у которых нет офферов
                    offers.forEach((offer: IGetCouponsDataFormatted): void => {
                        if (offer.id === localOfferId) {
                            findedCoupon = {
                                ...coupon,
                                logo: offersObject[localOfferId],
                                dateFrom: dateFrom.slice(0, 10),
                                dateTo: dateTo.slice(0, 10),
                                type: typesObject[localTypeId],
                            };
                        }
                    });
                    if (findedCoupon) {
                        return [...acc, findedCoupon];
                    }
                    return acc;
                    // показываем сначала персональные купоны, потом остальные
                }, [])
                .sort((a, b) => b.isPersonal - a.isPersonal);

            setIsCouponsLoading(false);
            return formattedCoupons;
        },
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
            revalidateFirstPage: false,
        },
    );

    const isLoadingData = useMemo(
        () => isLoadingTypes || isLoadingOffers || isLoadingCouponOfferIds,
        [isLoadingCouponOfferIds, isLoadingTypes, isLoadingOffers],
    );

    const filtersOnChange = (
        name: string,
        value:
            | string
            | {
                  label: string;
                  value: string | number | boolean;
              },
    ): void => {
        setFiltersDirty(true);
        setFilters({ ...filters, [name]: value });
        setSize(1);
    };

    const resetFilters = (): void => {
        if (JSON.stringify(filters) === JSON.stringify(defaultFiltersValue)) return;

        setFiltersDirty(false);
        setFilters({ ...defaultFiltersValue });
        setSize(1);
    };

    const copyText = async (text: string | Promise<string>): Promise<void> => {
        if (typeof text === 'string' && !text.trim()) return;
        try {
            await copyTextToClipboard(text);
            notification.success({
                message: tCommon('Copied'),
            });
        } catch (err) {
            notification.error({
                message: tCommon('Error'),
            });
        }
    };

    const createCreative = async (
        offerId: number,
        couponLink: string,
        promocode: string,
        placementId?: number,
        material?: string,
    ): Promise<IGetCreativesData | null> => {
        try {
            let creativeName: string = promocode?.substring(0, 99);

            // Сущетсвуют "акции" для которых нет промокода
            if (typeof creativeName === 'undefined') {
                const offerName: string = offers.find(item => item.id === offerId)?.name;
                creativeName = t('Coupon for offer name', {
                    offer_name: offerName || offerId,
                });
            }

            const createDeeplinkBody = {
                offerId,
                link: couponLink,
                type: 'deeplink',
                description: creativeName,
                placementId,
                material,
            };
            try {
                const { data: newCreativeData } = (await api.creative.createCreative({
                    body: createDeeplinkBody,
                })) as ICreateUseRequestInfiniteResponse<IGetCreativesData>;
                return newCreativeData;
            } catch (err) {
                const { errors } = await err.response.json();
                const [firstError] = errors;
                const { error } = firstError;
                const errorText = CREATE_CREATIVE_ERRORS[error] || CREATE_CREATIVE_ERRORS.default;

                notification.error({
                    message: tErrors(errorText),
                });

                console.error(errors);
                return null;
            }
        } catch (err) {
            console.error(err);
            notification.error({
                message: t('When creating a creative, an error occurred'),
            });
            return null;
        }
    };

    const copyPartnerLinkToClipboard = async (
        offerId: number,
        couponLink: string,
        promocode: string,
        placementId?: number,
        material?: string,
    ): Promise<void> => {
        try {
            logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.getCoupon, {
                offer_id: offerId,
            });

            notification.info({
                message: t('Generating link'),
            });

            const loadLink = async (): Promise<string> => {
                const deeplinkInfo: IGetCreativesData | null = await createCreative(
                    offerId,
                    couponLink,
                    promocode,
                    placementId,
                    material,
                );

                const isAli = offerId === ALI_ID && IS_ALI_LINK.test(couponLink);

                if (deeplinkInfo !== null) {
                    const deeplink = deeplinkInfo?.attributes?.code;
                    const preparedDeeplink = addParamsToDeeplink(deeplink, couponLink);

                    let reducedLink = preparedDeeplink;
                    if (!isAli) reducedLink = await reduceLink(preparedDeeplink);
                    couponLinksCache[couponLink] = reducedLink;

                    return reducedLink;
                }

                return '';
            };

            const link = await loadLink();

            if (link) copyText(link);
        } catch (e) {
            console.error(e);
        }
    };

    const onCreateLink = (coupon: ICouponProps) => {
        const isAli = coupon.offerId === ALI_ID && IS_ALI_LINK.test(coupon.url);
        if (isAli) {
            setPlacementModal(coupon);
            return;
        }

        copyPartnerLinkToClipboard(coupon.offerId, coupon.url, coupon.promocode);
    };

    const selectsOptions: ICouponSelectsOptions = {
        offers: prepareSelectOptions(offers || [], 'name'),
        types: prepareSelectOptions(types || [], 'description'),
    };

    const loadings: ICouponLoadingFlags = {
        offerId: isLoadingOffers,
        typeId: isLoadingTypes,
    };

    // Собранные данные от ответов
    const coupons: IGetCouponsDataFormatted[] = Array.isArray(couponsData)
        ? couponsData.flat()
        : [];

    // Если не пришел первый ответ
    const isLoadingInitialData: boolean = !couponsData && !couponsError;

    // Если следующий ответ от запроса ещё не пришел
    const isLoadingMore: boolean =
        isLoadingInitialData ||
        (size > 0 && couponsData && typeof couponsData[size - 1] === 'undefined');

    // Если уже первый ответ пришел пустой
    const isCouponsEmpty: boolean = useMemo(
        () => couponsData?.[0]?.length === 0 || !couponsData.length,
        [couponsData],
    );

    const isRequestsHaveError: boolean = couponsError || offersError || typesErrors;

    return (
        <div className="w-full mb-36">
            <div className="flex items-center justify-between mb-6">
                <h1 className="mr-4 font-medium text-[24px] sm:text-[28px] text-blueGray-600">
                    {t('Promo Codes and Coupons')}
                </h1>
                <div className="md:hidden">
                    <FilterButton
                        onClick={() => toggleFilters(!filtersIsVisible)}
                        icon={filtersIsVisible ? faTimes : faSlidersV}
                        filtersDirty={filtersIsVisible ? false : filtersDirty}
                    />
                </div>
            </div>

            <div className={`md:block ${filtersIsVisible ? 'block' : 'hidden'}`}>
                <Filters
                    values={filters}
                    loadings={loadings}
                    selectsOptions={selectsOptions}
                    onChange={filtersOnChange}
                    resetFilters={resetFilters}
                />
            </div>

            <p className="text-center text-blueGray-800">
                {isRequestsHaveError && <BaseEmpty message={t('Failed to load data on coupons')} />}

                {isCouponsEmpty && !isLoadingMore && (
                    <BaseEmpty message={t('There are no available coupons')} />
                )}

                {(isDataForCouponsReceived && !couponsData.length && !isCouponsLoading) ||
                    (!isDataForCouponsReceived && !isLoadingData && (
                        <BaseEmpty message={tCommon('Nothing found')} />
                    ))}
            </p>

            {(isLoadingTypes || isLoadingOffers) && (
                <div className="flex justify-center">
                    <Loader />
                </div>
            )}

            {!isCouponsEmpty && !isRequestsHaveError && (
                <div>
                    <Grid gutter={[24, 24]}>
                        {coupons.map(coupon => (
                            <Grid.Col key={coupon.id} span={24} sm={12} md={24} xl={12}>
                                <Coupon
                                    coupon={coupon}
                                    onDescriptionClick={copyText}
                                    onClick={() => onCreateLink(coupon)}
                                    handleModal={() => toggleFullDescriptionModal(coupon)}
                                    onCopyPromocode={copyText}
                                />
                            </Grid.Col>
                        ))}
                        {isLoadingMore &&
                            [...new Array(LOADING_STUBS)].map((_, i) => (
                                <Grid.Col key={i} span={24} xl={12}>
                                    <div
                                        style={{ height: 309 }}
                                        className="bg-blueGray-300 rounded-2 animate-pulse"
                                    />
                                </Grid.Col>
                            ))}
                    </Grid>
                </div>
            )}

            {hasNext && !isRequestsHaveError && (
                <div className="flex justify-center h-12 mt-5 mb-16">
                    <Button
                        type="primary"
                        className="w-full xs:w-auto"
                        onClick={() => setSize(size + 1)}
                        disabled={isLoadingMore}
                    >
                        {tCommon('Load more')}
                    </Button>
                </div>
            )}
            {isModalOpen && (
                <ModalFullDescription
                    onClose={() => toggleFullDescriptionModal(null)}
                    coupon={couponForModal}
                    getCouponLink={() => onCreateLink(couponForModal)}
                    onCopyPromocode={copyText}
                />
            )}

            {placementModal && (
                <ModalSelectPlacement
                    onClose={() => setPlacementModal(null)}
                    onLink={copyPartnerLinkToClipboard}
                    coupon={placementModal}
                />
            )}
        </div>
    );
};

export default WithRole(CouponsPage, [rolesEnum.user]);

export const getStaticProps: GetStaticProps = async ({
    params,
}): Promise<CustomGetStaticPropsResult> => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async (): Promise<CustomGetStaticPathsResult> => {
    return { paths: getLocalePaths(), fallback: false };
};
